import React from 'react';
import { Cartesian3DAxes } from '../common/Cartesian3DAxes.jsx';
import { DraggablePoint } from '../common/DraggablePoint.jsx';

export const IntersectingPlanesTriangleComponent = ({ params, onParamChange, svgRef, showCoordinates }) => {
  const {
    centerX = 200,
    centerY = 150,
    plane1Angle = 45,
    plane2Angle = 135,
    triangleSize = 60,
    planeFill = "rgba(100, 150, 255, 0.3)",
    triangleFill = "lightgreen",
    stroke = "black",
    strokeWidth = 2,
    showIntersection = true,
    showTriangle = true,
    showNormals = true,
    show3DAxes = true
  } = params;

  // Convert angles to radians
  const angle1Rad = (plane1Angle * Math.PI) / 180;
  const angle2Rad = (plane2Angle * Math.PI) / 180;
  
  // Plane dimensions
  const planeWidth = 120;
  const planeHeight = 80;
  
  // Calculate plane 1 vertices (rotated around center)
  const plane1Points = [
    {
      x: centerX + Math.cos(angle1Rad) * planeWidth / 2,
      y: centerY - planeHeight / 2
    },
    {
      x: centerX - Math.cos(angle1Rad) * planeWidth / 2,
      y: centerY - planeHeight / 2
    },
    {
      x: centerX - Math.cos(angle1Rad) * planeWidth / 2,
      y: centerY + planeHeight / 2
    },
    {
      x: centerX + Math.cos(angle1Rad) * planeWidth / 2,
      y: centerY + planeHeight / 2
    }
  ];
  
  // Calculate plane 2 vertices
  const plane2Points = [
    {
      x: centerX + Math.cos(angle2Rad) * planeWidth / 2,
      y: centerY - planeHeight / 2
    },
    {
      x: centerX - Math.cos(angle2Rad) * planeWidth / 2,
      y: centerY - planeHeight / 2
    },
    {
      x: centerX - Math.cos(angle2Rad) * planeWidth / 2,
      y: centerY + planeHeight / 2
    },
    {
      x: centerX + Math.cos(angle2Rad) * planeWidth / 2,
      y: centerY + planeHeight / 2
    }
  ];
  
  // Triangle at intersection (equilateral)
  const trianglePoints = [
    { x: centerX, y: centerY - triangleSize },
    { x: centerX - triangleSize * 0.866, y: centerY + triangleSize / 2 },
    { x: centerX + triangleSize * 0.866, y: centerY + triangleSize / 2 }
  ];
  
  // Normal vectors
  const normal1 = {
    x: centerX + Math.cos(angle1Rad + Math.PI / 2) * 40,
    y: centerY - 40
  };
  
  const normal2 = {
    x: centerX + Math.cos(angle2Rad + Math.PI / 2) * 40,
    y: centerY - 40
  };

  return (
    <g>
      {/* 3D Axes */}
      {show3DAxes && <Cartesian3DAxes centerX={centerX} centerY={centerY + 60} size={50} />}
      
      {/* Plane 1 */}
      <polygon
        points={plane1Points.map(p => `${p.x},${p.y}`).join(' ')}
        fill={planeFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Plane 2 */}
      <polygon
        points={plane2Points.map(p => `${p.x},${p.y}`).join(' ')}
        fill={planeFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Intersection line */}
      {showIntersection && (
        <line
          x1={centerX}
          y1={centerY - planeHeight / 2 - 10}
          x2={centerX}
          y2={centerY + planeHeight / 2 + 10}
          stroke="purple"
          strokeWidth={strokeWidth * 1.5}
          strokeDasharray="5,5"
        />
      )}
      
      {/* Triangle at intersection */}
      {showTriangle && (
        <>
          <polygon
            points={trianglePoints.map(p => `${p.x},${p.y}`).join(' ')}
            fill={triangleFill}
            stroke="darkgreen"
            strokeWidth={strokeWidth}
          />
          {/* Triangle vertices labels */}
          <text x={trianglePoints[0].x} y={trianglePoints[0].y - 10} fontSize="11" fill="darkgreen" textAnchor="middle">A</text>
          <text x={trianglePoints[1].x - 15} y={trianglePoints[1].y + 5} fontSize="11" fill="darkgreen">B</text>
          <text x={trianglePoints[2].x + 15} y={trianglePoints[2].y + 5} fontSize="11" fill="darkgreen">C</text>
        </>
      )}
      
      {/* Normal vectors */}
      {showNormals && (
        <>
          <defs>
            <marker id="arrowNormal" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill="orange" />
            </marker>
          </defs>
          <line
            x1={centerX}
            y1={centerY - 40}
            x2={normal1.x}
            y2={normal1.y}
            stroke="orange"
            strokeWidth={strokeWidth}
            markerEnd="url(#arrowNormal)"
          />
          <line
            x1={centerX}
            y1={centerY - 40}
            x2={normal2.x}
            y2={normal2.y}
            stroke="darkorange"
            strokeWidth={strokeWidth}
            markerEnd="url(#arrowNormal)"
          />
        </>
      )}
      
      {/* Labels */}
      <text x={plane1Points[0].x + 10} y={plane1Points[0].y - 10} fontSize="12" fill="blue" fontWeight="bold">
        Plane 1 ({plane1Angle}°)
      </text>
      <text x={plane2Points[0].x - 40} y={plane2Points[0].y - 10} fontSize="12" fill="blue" fontWeight="bold">
        Plane 2 ({plane2Angle}°)
      </text>
      
      {/* Draggable points */}
      {onParamChange && (
        <>
          <DraggablePoint
            cx={centerX}
            cy={centerY}
            label="Center"
            coords={showCoordinates ? `(${centerX}, ${centerY})` : ''}
            onDrag={(newX, newY) => {
              onParamChange('centerX', newX);
              onParamChange('centerY', newY);
            }}
            svgRef={svgRef}
          />
          <DraggablePoint
            cx={trianglePoints[0].x}
            cy={trianglePoints[0].y}
            label="△"
            coords={showCoordinates ? `size=${triangleSize.toFixed(0)}` : ''}
            onDrag={(newX, newY) => {
              const newSize = Math.abs(centerY - newY);
              onParamChange('triangleSize', Math.max(20, Math.min(120, newSize)));
            }}
            svgRef={svgRef}
          />
        </>
      )}
    </g>
  );
};
